const MANAGED_KEYS = ['_info_banner', '_quantity_shortage'];
const SHORTAGE_VALUE = 'Quantity Shortage';
const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
const MAX_PASSES = 8;
const DEBOUNCE = 150;

let running = false;
let queued = false;
let internal = false;
let timer = null;

function cartRoot() {
  return window.Shopify?.routes?.root ?? '/';
}

async function getCart() {
  const response = await fetch(`${cartRoot()}cart.js`, { headers: { Accept: 'application/json' } });

  return response.ok ? response.json() : null;
}

async function postCart(path, body) {
  internal = true;

  try {
    const response = await fetch(`${cartRoot()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    return response.ok;
  } finally {
    internal = false;
  }
}

function readLineData(scope) {
  const map = new Map();

  scope.querySelectorAll('[data-cart-line-data]').forEach((element) => {
    const variantId = Number(element.dataset.variantId);

    if (!Number.isFinite(variantId)) return;

    map.set(variantId, {
      tracked: element.dataset.tracked === 'true',
      available: Number(element.dataset.available),
      infoBanner: element.dataset.infoBanner ?? '',
    });
  });

  return map;
}

async function fetchLineData() {
  try {
    const response = await fetch(`${cartRoot()}?sections=cart-drawer`, { headers: { Accept: 'application/json' } });

    if (!response.ok) return new Map();

    const sections = await response.json();
    const html = sections['cart-drawer'];

    if (!html) return new Map();

    return readLineData(new DOMParser().parseFromString(html, 'text/html'));
  } catch {
    return new Map();
  }
}

async function lineDataForCart(cart) {
  const data = readLineData(document);

  if (cart.items.every((item) => data.has(item.variant_id))) return data;

  const fetched = await fetchLineData();

  fetched.forEach((value, key) => {
    if (!data.has(key)) data.set(key, value);
  });

  return data;
}

function splitProperties(properties) {
  const managed = {};
  const rest = {};

  Object.entries(properties ?? {}).forEach(([key, value]) => {
    if (MANAGED_KEYS.includes(key)) {
      managed[key] = value;
    } else {
      rest[key] = value;
    }
  });

  return { managed, rest };
}

function mergeSignature(item) {
  const { rest } = splitProperties(item.properties);
  const keys = Object.keys(rest).sort();

  return `${item.variant_id}|${keys.map((key) => `${key}=${rest[key]}`).join('&')}`;
}

function targetProperties(wanted, totalQuantity) {
  const target = {};

  if (wanted.infoBanner) target._info_banner = wanted.infoBanner;

  if (wanted.tracked && Number.isFinite(wanted.available) && totalQuantity > wanted.available) {
    target._quantity_shortage = SHORTAGE_VALUE;
  }

  return target;
}

async function runPass() {
  const cart = await getCart();

  if (!cart?.items?.length) return false;

  const lineData = await lineDataForCart(cart);

  if (lineData.size === 0) return false;

  const groups = new Map();

  cart.items.forEach((item, index) => {
    const signature = mergeSignature(item);

    if (!groups.has(signature)) groups.set(signature, []);

    groups.get(signature).push({ item, index });
  });

  const duplicates = [...groups.values()].filter((group) => group.length > 1);

  if (duplicates.length > 0) {
    const updates = cart.items.map((item) => item.quantity);

    duplicates.forEach((group) => {
      updates[group[0].index] = group.reduce((total, entry) => total + entry.item.quantity, 0);
      group.slice(1).forEach((entry) => {
        updates[entry.index] = 0;
      });
    });

    return postCart('cart/update.js', { updates });
  }

  const totals = new Map();

  cart.items.forEach((item) => {
    totals.set(item.variant_id, (totals.get(item.variant_id) ?? 0) + item.quantity);
  });

  for (const item of cart.items) {
    const wanted = lineData.get(item.variant_id);

    if (!wanted) continue;

    const target = targetProperties(wanted, totals.get(item.variant_id));
    const { managed, rest } = splitProperties(item.properties);
    const unchanged = MANAGED_KEYS.every((key) => (managed[key] ?? '') === (target[key] ?? ''));

    if (unchanged) continue;

    const properties = { ...rest, ...target };

    return postCart('cart/change.js', { id: item.key, quantity: item.quantity, properties });
  }

  return false;
}

async function reconcile() {
  if (running) {
    queued = true;
    return;
  }

  running = true;

  let mutated = false;

  try {
    for (let pass = 0; pass < MAX_PASSES; pass += 1) {
      const changed = await runPass();

      if (!changed) break;

      mutated = true;
    }
  } catch (error) {
    console.warn('[cart-line-properties]', error);
  } finally {
    running = false;

    if (mutated) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    if (queued) {
      queued = false;
      schedule();
    }
  }
}

function schedule() {
  clearTimeout(timer);
  timer = setTimeout(reconcile, DEBOUNCE);
}

function watchFetch() {
  const originalFetch = window.fetch;

  if (typeof originalFetch !== 'function') return;

  window.fetch = function patchedFetch(...args) {
    const result = originalFetch.apply(this, args);

    try {
      const input = args[0];
      const url = typeof input === 'string' ? input : (input?.url ?? '');

      if (!internal && CART_MUTATION.test(url)) {
        result.then(() => schedule()).catch(() => {});
      }
    } catch {

    }

    return result;
  };
}

function watchXhr() {
  const open = window.XMLHttpRequest?.prototype?.open;

  if (typeof open !== 'function') return;

  window.XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
    try {
      if (!internal && typeof url === 'string' && CART_MUTATION.test(url)) {
        this.addEventListener('load', () => schedule());
      }
    } catch {

    }

    return open.call(this, method, url, ...rest);
  };
}

class CartLineData extends HTMLElement {
  connectedCallback() {
    schedule();
  }
}

if (!window.customElements.get('cart-line-data')) {
  window.customElements.define('cart-line-data', CartLineData);
}

document.addEventListener('cart:change', schedule);
document.addEventListener('cart:refresh', schedule);

watchFetch();
watchXhr();
schedule();
